import { stringify } from 'query-string';
import { mutate } from 'swr';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { apiUrl } from '@dashboard/services/env';
import request, { fetchInfinite } from '@dashboard/services/request';
import { fetcher } from '@dashboard/services/request';

export const updateSeller = async (token, values, sellerId, setSeller) => {
  try {
    const id = sellerId || (values && values._id);
    const key = `/sellers/${id}`;

    const response = await request(apiUrl + key, token, {
      body: JSON.stringify(values),
      method: 'POST',
    });

    if (!response) {
      throw new Error();
    }

    if (response?.status === 'error') {
      return { error: response };
    }

    if (response.data) {
      setSeller(response.data);
    }

    return { data: response.data, error: null };
  } catch (error) {
    if (error && error.message) {
      return { error: error.message };
    }

    return { error: 'Unable to save seller', data: null };
  }
};

export const getSeller = async (token, sellerId, query = {}) => {
  const qs = stringify(query);

  const key = `/sellers/${sellerId}`;

  const seller = await fetcher(`${key}?${qs}`, token);

  mutate(key, seller);

  return seller;
};

// react-query
export const getSellerAddress = async (token, sellerId) => {
  const key = `/sellers/${sellerId}/billing-address`;

  return fetcher(key, token);
};

export const getSellerUsage = async (token, sellerId) => {
  const key = `/sellers/${sellerId}/usage`;

  return fetcher(key, token);
};

export const getInvoices = async (sellerId, query = {}, token) => {
  return fetchInfinite(`/sellers/${sellerId}/invoices?${stringify(query)}`, token);
};

export const getInvoice = async (sellerId, invoiceId, token) => {
  return fetcher(`/sellers/${sellerId}/invoices/${invoiceId}`, token);
};

export const getMembers = async (sellerId, query = {}, token) => {
  return fetcher(`/sellers/${sellerId}/users?${stringify(query)}`, token);
};

export const inviteMember = async (token, sellerId, email) => {
  return fetcher(`/sellers/${sellerId}/users`, token, {
    method: 'POST',
    body: JSON.stringify({ email, role: 'admin' }),
  });
};

export const deleteMember = async (token, sellerId, userId) => {
  return fetcher(`/sellers/${sellerId}/users/${userId}`, token, {
    method: 'DELETE',
  });
};

export const changeAccountOwner = async (token, sellerId, userId) => {
  return fetcher(`/sellers/${sellerId}/change-owner`, token, {
    method: 'POST',
    body: JSON.stringify({ userId: userId }),
  });
};

export const getInvites = async (sellerId, token) => {
  return fetcher(`/sellers/${sellerId}/invites`, token);
};

export const revokeInvite = async (token, sellerId, inviteId) => {
  return fetcher(`/sellers/${sellerId}/invites/${inviteId}`, token, {
    method: 'DELETE',
  });
};

export const resendInvite = async (token, sellerId, inviteId) => {
  return fetcher(`/sellers/${sellerId}/invites/${inviteId}/resend`, token, {
    method: 'POST',
  });
};

export const createEmailDomain = async (token, sellerId, emailDomain) => {
  return fetcher(`/sellers/${sellerId}/email-domain`, token, {
    method: 'POST',
    body: JSON.stringify({ emailDomain }),
  });
};

export const verifyEmailDomain = async (token, sellerId) => {
  return fetcher(`/sellers/${sellerId}/email-domain/verify`, token, {
    method: 'POST',
  });
};

export const deleteEmailDomain = async (token, sellerId) => {
  return fetcher(`/sellers/${sellerId}/email-domain`, token, {
    method: 'DELETE',
  });
};

export const createSenderSignature = async (token, sellerId, { emailAddress, name }) => {
  return fetcher(`/sellers/${sellerId}/sender-signature`, token, {
    method: 'POST',
    body: JSON.stringify({ emailAddress, name }),
  });
};

export const updateSenderSignature = async (token, sellerId, { emailAddress, name }) => {
  return fetcher(`/sellers/${sellerId}/sender-signature`, token, {
    method: 'PUT',
    body: JSON.stringify({ emailAddress, name }),
  });
};

export const verifySenderSignature = async (token, sellerId) => {
  return fetcher(`/sellers/${sellerId}/sender-signature/verify`, token, {
    method: 'POST',
  });
};

export const deleteSenderSignature = async (token, sellerId) => {
  return fetcher(`/sellers/${sellerId}/sender-signature`, token, {
    method: 'DELETE',
  });
};

export const sendTestEmail = async (token, sellerId) => {
  return fetcher(`/sellers/${sellerId}/sender-signature/test-email`, token, {
    method: 'POST',
  });
};

export const getSellerOutstandingInvoice = async (token, sellerId) => {
  return fetcher(`/sellers/${sellerId}/outstanding-invoice`, token);
};

export const getEventOverview = async (token, sellerId, pageId) => {
  return fetcher(`/sellers/${sellerId}/event/${pageId}/overview`, token);
};

export const getCheckoutOverview = async (token, sellerId, pageId) => {
  return fetcher(`/sellers/${sellerId}/pages/${pageId}/overview`, token);
};

export default function useSellerQueries() {
  const { token, sellerId, setSeller } = useAuth();

  return {
    getSeller: (sellerId, query) => getSeller(token, sellerId, query),
    updateSeller: values => updateSeller(token, values, sellerId, setSeller),
    getSellerAddress: sellerId => getSellerAddress(token, sellerId),
    getInvoice: (sellerId, invoiceId) => getInvoice(sellerId, invoiceId, token),
    getInvoices: (sellerId, query) => getInvoices(sellerId, query, token),
    getMembers: (sellerId, query) => getMembers(sellerId, query, token),
    inviteMember: ({ sellerId, email }) => inviteMember(token, sellerId, email),
    deleteMember: ({ sellerId, userId }) => deleteMember(token, sellerId, userId),
    changeAccountOwner: ({ sellerId, userId }) => changeAccountOwner(token, sellerId, userId),
    createEmailDomain: ({ emailDomain }) => createEmailDomain(token, sellerId, emailDomain),
    verifyEmailDomain: () => verifyEmailDomain(token, sellerId),
    deleteEmailDomain: () => deleteEmailDomain(token, sellerId),
    createSenderSignature: ({ emailAddress, name }) =>
      createSenderSignature(token, sellerId, { emailAddress, name }),
    updateSenderSignature: ({ emailAddress, name }) =>
      updateSenderSignature(token, sellerId, { emailAddress, name }),
    verifySenderSignature: () => verifySenderSignature(token, sellerId),
    deleteSenderSignature: () => deleteSenderSignature(token, sellerId),
    sendTestEmail: () => sendTestEmail(token, sellerId),
    getEventOverview: ({ sellerId, pageId }) => getEventOverview(token, sellerId, pageId),
    getCheckoutOverview: ({ sellerId, pageId }) => getCheckoutOverview(token, sellerId, pageId),
    getInvites: sellerId => getInvites(sellerId, token),
    revokeInvite: ({ sellerId, inviteId }) => revokeInvite(token, sellerId, inviteId),
    resendInvite: ({ sellerId, inviteId }) => resendInvite(token, sellerId, inviteId),
  };
}
